import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Plus, Trash2, Flame } from "lucide-react";
import { BASE, adminApi } from "@/lib/api";
import { selectCls } from "../managed/shared";
import { HeatCanvas, VW } from "./HeatCanvas";

const SNIP = (id) => `<script src="${BASE}/api/hm/s.js" data-site="${id}" async></script>`;

const Sites = ({ sites, site, setSite, reload }) => {
  const [url, setUrl] = useState("");
  const add = () => adminApi.hmAddSite({ url }).then((s) => { setUrl(""); toast.success("Sito aggiunto"); reload(s.id); }).catch((e) => toast.error(e?.response?.data?.detail || "Errore"));
  const del = () => window.confirm("Eliminare il sito e tutti i suoi dati heatmap?") && adminApi.hmDelSite(site).then(() => reload(null));
  const cur = sites.find((s) => s.id === site);
  return (
    <div className="border border-[#d5dbde] bg-white p-4 space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="hm-site-select" value={site || ""} onChange={(e) => setSite(e.target.value)} className={`${selectCls} md:w-80`}>
          <option value="">{sites.length ? "Seleziona sito…" : "Nessun sito"}</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.host}</option>)}
        </select>
        {cur && <button data-testid="hm-site-delete" onClick={del} className="text-[#6b7280] hover:text-[#E0262C]"><Trash2 className="w-4 h-4" /></button>}
        <input data-testid="hm-site-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://sito-cliente.ch"
          className="h-9 px-3 text-sm border border-[#d5dbde] rounded-sm flex-1 min-w-[200px] focus:outline-none focus:border-[#2B3036]" />
        <button data-testid="hm-site-add" onClick={add} disabled={!url} className="h-9 px-3 text-xs inline-flex items-center gap-1 border border-[#2B3036] hover:bg-[#2B3036] hover:text-white disabled:opacity-40 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Aggiungi sito
        </button>
      </div>
      {cur && (
        <div className="flex items-center gap-2">
          <code data-testid="hm-snippet" className="flex-1 text-[11px] bg-[#f4f6f7] border border-[#d5dbde] px-2 py-1.5 overflow-x-auto whitespace-nowrap">{SNIP(cur.id)}</code>
          <button data-testid="hm-snippet-copy" onClick={() => navigator.clipboard.writeText(SNIP(cur.id)).then(() => toast.success("Snippet copiato"))}
            className="h-8 px-2 border border-[#d5dbde] hover:border-[#2B3036]"><Copy className="w-3.5 h-3.5" /></button>
        </div>
      )}
      <p className="text-[10px] text-[#6b7280]">Snippet da inserire prima di &lt;/body&gt; (o via Tag Manager). Raccoglie click, scroll e movimento solo dopo consenso del visitatore, senza IP né dati personali; cancellazione automatica dopo 90 giorni.</p>
    </div>
  );
};

export const HeatmapPanel = () => {
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [f, setF] = useState({ days: 30, path: "", kind: "click", device: "" });
  const [opacity, setOpacity] = useState(0.7);
  const [d, setD] = useState(null);
  const canvas = useRef(null);
  const reload = (sel) => adminApi.hmSites().then((r) => { setSites(r.sites); setSite(sel === undefined ? (r.sites[0]?.id || "") : sel || ""); });
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!site) { setD(null); return; }
    adminApi.hmData({ site, days: f.days, path: f.path || undefined, device: f.device || undefined }).then(setD).catch(() => toast.error("Errore dati heatmap"));
  }, [site, f.days, f.path, f.device]);
  const cur = sites.find((s) => s.id === site);
  const h = Math.max(900, Math.min(d?.page_height || 1600, 8000));
  const exportPng = () => {
    const a = document.createElement("a");
    a.href = canvas.current.toDataURL("image/png");
    a.download = `heatmap-${cur?.host}-${f.kind}-${f.days}gg.png`;
    a.click();
  };
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: k === "days" ? Number(e.target.value) : e.target.value }));
  return (
    <div data-testid="admin-heatmap" className="space-y-4">
      <Sites sites={sites} site={site} setSite={setSite} reload={reload} />
      {cur && (
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="hm-path" value={f.path} onChange={set("path")} className={selectCls}><option value="">Tutte le pagine</option>{(d?.paths || []).map((p) => <option key={p} value={p}>{p}</option>)}</select>
          <select data-testid="hm-days" value={f.days} onChange={set("days")} className={selectCls}>{[7, 30, 90].map((x) => <option key={x} value={x}>{x} giorni</option>)}</select>
          <select data-testid="hm-kind" value={f.kind} onChange={set("kind")} className={selectCls}><option value="click">Click</option><option value="move">Movimento</option><option value="scroll">Scroll</option></select>
          <select data-testid="hm-device" value={f.device} onChange={set("device")} className={selectCls}><option value="">Tutti i device</option><option value="desktop">Desktop</option><option value="tablet">Tablet</option><option value="mobile">Mobile</option></select>
          <label className="flex items-center gap-2 text-xs">Opacità <input data-testid="hm-opacity" type="range" min="0.1" max="1" step="0.05" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} /></label>
          <button data-testid="hm-export" onClick={exportPng} className="ml-auto h-9 px-3 text-xs inline-flex items-center gap-1 border border-[#2B3036] hover:bg-[#2B3036] hover:text-white transition-colors"><Download className="w-3.5 h-3.5" /> Export PNG</button>
        </div>
      )}
      {d && (
        <div data-testid="hm-stats" className="flex gap-6 text-xs font-mono text-[#5b6168]">
          <span><Flame className="inline w-3.5 h-3.5 text-[#E0262C]" /> {d.sessions} sessioni</span><span>Durata media {d.avg_duration_s}s</span>
          <span>{d.clicks.length} zone di click</span>
        </div>
      )}
      {cur && (
        <div className="border border-[#d5dbde] bg-white overflow-auto max-h-[75vh]">
          <div className="relative origin-top-left mx-auto" style={{ width: VW, height: h }}>
            <iframe title="sito" src={`${cur.url.replace(/\/$/, "")}${f.path || ""}`} className="absolute inset-0 w-full h-full border-0" sandbox="allow-same-origin" scrolling="no" />
            <div className="absolute inset-0" style={{ opacity }}><HeatCanvas data={d} kind={f.kind} height={h} canvasRef={canvas} /></div>
          </div>
        </div>
      )}
      {cur && <p className="text-[10px] text-[#6b7280]">Se il sito blocca l'anteprima in iframe, la mappa resta visibile sullo sfondo bianco (coordinate su larghezza {VW}px).</p>}
    </div>
  );
};
